import { PropsWithChildren, useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '../../contexts/auth-context';
import { getPublicEnvVar, isProduction } from '@upcaret/env-utils';
import type { PostHogConfig } from 'posthog-js';

const posthogKey =
  getPublicEnvVar('VITE_CENTLY_PUBLIC_POSTHOG_KEY') ||
  getPublicEnvVar('VITE_PUBLIC_POSTHOG_KEY');
const posthogHost = isProduction() ? '/ph' : 'https://us.i.posthog.com';
const posthogEnabled = Boolean(posthogKey) && isProduction();

export function AppProviders({ children }: PropsWithChildren) {
  const { options } = useRouter();
  const queryClient = options.context.queryClient;
  const router = useRouter();

  useEffect(() => {
    if (!posthogEnabled || typeof window === 'undefined') return;

    let unsubscribeRoute: (() => void) | null = null;
    let posthog: typeof import('posthog-js') | null = null;

    const posthogOptions: Partial<PostHogConfig> = {
      api_host: posthogHost,
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: isProduction(),
      session_recording: {
        recordCrossOriginIframes: false,
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
      loaded: (client) => {
        client.register({ platform: 'web' });
        client.capture('$pageview');
        unsubscribeRoute = router.subscribe('onResolved', (event) => {
          if (event.hrefChanged || event.pathChanged || event.hashChanged) {
            client.capture('$pageview');
          }
        });
      },
    };

    import('posthog-js')
      .then((mod) => {
        posthog = mod;
        posthog.default.init(posthogKey!, posthogOptions);
      })
      .catch((err) => {
        console.error('PostHog init failed', err);
      });

    return () => {
      unsubscribeRoute?.();
    };
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>{children}</AuthProvider>
    </QueryClientProvider>
  );
}
